import type { AssistantMessage, ConversationMessage } from './types';
import { getContextLimit, isAssistantMessage, isSystemMessage } from './types';

/**
 * Fraction of the model's context limit at which we ask the Responses API to compact the
 * conversation. The request that crosses the threshold is still billed at its full size, and
 * the window keeps growing until the next check, so this leaves headroom below the limit (which
 * for GPT-5.6 sits under the 272K long-context repricing cliff).
 */
export const COMPACTION_THRESHOLD_FRACTION = 0.75;

export function getCompactionThreshold(model: string): number {
    return Math.floor(getContextLimit(model) * COMPACTION_THRESHOLD_FRACTION);
}

export function hasCompactionItems(message: ConversationMessage): message is AssistantMessage {
    return isAssistantMessage(message) && (message.compaction_items?.length ?? 0) > 0;
}

export interface CompactionSlice {
    messages: ConversationMessage[];
    /** Messages before the compaction point that were left out of the request. */
    droppedCount: number;
}

/**
 * Restricts the request to what the model still needs once a compaction item exists: the base
 * prompt (the leading run of system messages) plus everything from the most recent compaction
 * item onward. The compaction item stands in for the messages before it, so sending them too
 * would only waste tokens and break the cached prefix.
 */
export function sliceAtLatestCompaction(messages: ConversationMessage[]): CompactionSlice {
    // index of the message carrying the most recent compaction item
    const cutIndex = messages.findLastIndex(hasCompactionItems);

    // if no compaction has ever occurred, return as is
    if (cutIndex === -1) {
        return { messages, droppedCount: 0 };
    }

    // the first few consecutive messages are the system prompt. the ResponsesAPIAdapter sends
    // them as instructions with extractLeadingInstructions so they don't get included in the
    // compaction and still need to be sent.
    let leadingSystemEnd = 0;
    while (leadingSystemEnd < cutIndex && isSystemMessage(messages[leadingSystemEnd])) {
        leadingSystemEnd++;
    }

    // send the system prompts and all messages since & including the most recent compaction
    const kept = [...messages.slice(0, leadingSystemEnd), ...messages.slice(cutIndex)];
    return { messages: kept, droppedCount: messages.length - kept.length };
}
